/**
 * Intelligems A/B: assigns window.__igActiveOffer, updates promo UI, and syncs
 * the cart attribute igTestGroups so Shopify Functions can gate discounts.
 *
 * Cart attribute igTestGroups (must match app Functions):
 * - groupA OR Intelligems ids …: Escalating discount
 * - groupD / groupE: sale price cart transform + checkout
 * - groupB OR Intelligems id (settings): B2G1 / B3G1 + FS @ 2 (control / default)
 * - groupF OR Intelligems id (settings): B2G1 + FS @ 4
 * - groupC OR Intelligems ids …: stacked escalating / pack tiers
 * - Values may be comma-separated (e.g. "599855518f37,982eb52ae514").
 * - neutral / empty: default B2G1 + FS @ 2
 *
 * Promo copy is applied from cart.attributes.igTestGroups:
 * - groupB: default Liquid (B2G1)
 * - groupA: data-ig-escalating-text
 * - groupC: data-ig-groupc-text on announcement, PDP deal, hero, cart banner
 * - groupD / groupE: data-ig-groupd-text / data-ig-groupe-text (falls back to escalating text)
 * - Free shipping lines: [data-ig-fs-text] + Control vs groupF settings / data attrs
 * Theme settings ig_force_default_group_a / ig_force_default_group_b / ig_force_default_group_c
 * seed the cart when Intelligems detect() has no offer. Force A takes precedence over C and B.
 */
(function () {
  'use strict';

  // Test 1: Escalating Discount vs Escalating Bundles
  const TEST_1_ID = '__REPLACE_TEST_1_EXPERIMENT_ID__';
  const TEST_1_GROUPS = {
    '__REPLACE_TEST_1_GROUP_A__': 'escalating_discount',
    '__REPLACE_TEST_1_GROUP_B__': 'escalating_bundles',
  };

  // Test 2: Buy 2 Get 1 vs Buy 3 Get 1
  const TEST_2_ID = '__REPLACE_TEST_2_EXPERIMENT_ID__';
  const TEST_2_GROUPS = {
    '__REPLACE_TEST_2_GROUP_A__': 'buy_2_get_1',
    '__REPLACE_TEST_2_GROUP_B__': 'buy_3_get_1',
  };

  const OFFER_CONFIGS = {
    escalating_discount: {
      bannerText: 'Get Up to 30% OFF',
      pdpPromoText: '10% off 1+ | 20% off 3+ | 30% OFF 5+',
    },
    escalating_bundles: {
      bannerText: 'Bundle & Save up to 40%!',
      pdpPromoText: 'Save up to 40% with Bundle Packs!',
    },
    buy_2_get_1: {
      triggerQty: 3,
      maxFreeItems: 4,
      bannerText: 'Buy 2 Get 1 Free',
      pdpPromoText: 'Buy 2, Get 1 FREE!',
    },
    buy_3_get_1: {
      triggerQty: 4,
      maxFreeItems: 3,
      bannerText: 'Buy 3 Get 1 Free',
      pdpPromoText: 'Buy 3, Get 1 FREE!',
    },
  };

  /**
   * Maps active offer to cart igTestGroups (groupA = escalating, groupB = B2G1, groupC = stacked packs).
   */
  const CART_GROUP_BY_OFFER = {
    escalating_discount: 'groupA',
    buy_2_get_1: 'groupB',
    buy_3_get_1: 'groupB',
    escalating_bundles: 'groupC',
  };

  // Cached group value — lets us apply copy synchronously after any DOM replacement
  var _cachedGroup = null;

  function experimentsArePlaceholders() {
    return (
      String(TEST_1_ID).startsWith('__REPLACE') &&
      String(TEST_2_ID).startsWith('__REPLACE')
    );
  }

  /** Lets cart-dependent UI (e.g. the PDP offer progress bar) re-render for the new group. */
  function notifyCartGroupUpdated() {
    try {
      document.dispatchEvent(new CustomEvent('ig:cart-group-updated'));
    } catch (e) {}
  }

  function syncCartAttributeForOffer(offerKey) {
    if (getPreviewGroupFromUrl()) return Promise.resolve();
    var group = CART_GROUP_BY_OFFER[offerKey];
    if (group === undefined) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { igTestGroups: group } }),
    }).then(function () {
      _cachedGroup = group;
      if (group && VALID_GROUPS[group]) {
        try { sessionStorage.setItem('igTestGroup', group); } catch (e) {}
      }
      notifyCartGroupUpdated();
    }).catch(function (e) {
      console.warn('[IG Orchestrator] Cart attribute update failed', e);
    });
  }

  function clearCartAttribute() {
    _cachedGroup = null;
    try { sessionStorage.removeItem('igTestGroup'); } catch (e) {}
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { igTestGroups: '' } }),
    }).then(notifyCartGroupUpdated).catch(function (e) {
      console.warn('[IG Orchestrator] Cart attribute clear failed', e);
    });
  }

  /** Recognized igTestGroups values (labels + Intelligems group ids). */
  var GROUP_A_VALUES = {
    groupA: true,
    '2b0c64d032fb': true,
    'd55b9f4463dc': true,
  };
  var GROUP_D_VALUES = {
    groupD: true,
    '1b9b21442ba2': true,
    '583d5923a533': true,
    'b34435223162': true,
  };
  var GROUP_E_VALUES = {
    groupE: true,
    '401fa6708173': true,
    'a8db065641f8': true,
    'c8721924c5f9': true,
  };
  var GROUP_B_VALUES = {
    groupB: true,
    '982eb52ae514': true,
    '8eef2d7bea97': true,
  };
  var GROUP_F_VALUES = { groupF: true };
  var FS_CONTROL_VALUES = {};
  var GROUP_C_VALUES = {
    groupC: true,
    'c2f3cc63ce00': true,
    'a64fff5fac4d': true,
  };

  function igTokens(g) {
    if (g == null || g === '') return [];
    return String(g)
      .split(',')
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  /** True if any comma-separated segment is in the map (object used as a set). */
  function matchesAnyToken(g, map) {
    var ts = igTokens(g);
    for (var i = 0; i < ts.length; i++) {
      if (map[ts[i]]) return true;
    }
    return false;
  }

  function isGroupA(g) {
    return matchesAnyToken(g, GROUP_A_VALUES);
  }
  function isGroupB(g) {
    return matchesAnyToken(g, GROUP_B_VALUES);
  }
  function isGroupC(g) {
    return matchesAnyToken(g, GROUP_C_VALUES);
  }
  function isGroupD(g) {
    return matchesAnyToken(g, GROUP_D_VALUES);
  }
  function isGroupE(g) {
    return matchesAnyToken(g, GROUP_E_VALUES);
  }
  function isGroupF(g) {
    return matchesAnyToken(g, GROUP_F_VALUES);
  }
  function isFsControl(g) {
    return matchesAnyToken(g, FS_CONTROL_VALUES);
  }

  /** B2G1 family: keep force-default from wiping IG FS hashes paired with groupB. */
  function isB2g1FamilyCart(g) {
    return isGroupB(g) || isGroupF(g) || isFsControl(g);
  }

  /** Price test arm for display: groupE > groupD > groupA (matches Liquid resolver). */
  function resolvePriceTestArm(g) {
    if (isGroupE(g)) return 'groupE';
    if (isGroupD(g)) return 'groupD';
    if (isGroupA(g)) return 'groupA';
    return null;
  }

  function resolveFreeShipMinQty(g) {
    var cfg = window.__IG_PRICE_TEST || {};
    if (isGroupF(g)) return Number(cfg.groupFMinQty) || 4;
    return Number(cfg.fsControlMinQty) || 2;
  }

  function getFsCopyForSurface(g, surface) {
    var cfg = window.__IG_PRICE_TEST || {};
    var isF = isGroupF(g);
    if (surface === 'cart') {
      if (isF) return cfg.fsTextGroupFCart || cfg.fsTextGroupF || null;
      return cfg.fsTextControlCart || cfg.fsTextControl || null;
    }
    if (surface === 'announcement') {
      if (isF) return cfg.fsTextGroupFAnnouncement || cfg.fsTextGroupF || null;
      return cfg.fsTextControlAnnouncement || cfg.fsTextControl || null;
    }
    if (surface === 'pdp') {
      if (isF) return cfg.fsTextGroupFPdp || cfg.fsTextGroupF || null;
      return cfg.fsTextControlPdp || cfg.fsTextControl || null;
    }
    if (surface === 'pdp-accordion') {
      if (isF) return cfg.fsTextGroupFPdpAccordion || cfg.fsTextGroupFPdp || cfg.fsTextGroupF || null;
      return cfg.fsTextControlPdpAccordion || cfg.fsTextControlPdp || cfg.fsTextControl || null;
    }
    if (isF) return cfg.fsTextGroupF || null;
    return cfg.fsTextControl || null;
  }

  var ATTR_ESCALATING = 'data-ig-escalating-text';
  var ATTR_GROUPC = 'data-ig-groupc-text';
  var ATTR_GROUPD = 'data-ig-groupd-text';
  var ATTR_GROUPE = 'data-ig-groupe-text';

  function getPriceTestPromoText(el, g) {
    if (isGroupE(g)) {
      return el.getAttribute(ATTR_GROUPE) || el.getAttribute(ATTR_ESCALATING) || null;
    }
    if (isGroupD(g)) {
      return el.getAttribute(ATTR_GROUPD) || el.getAttribute(ATTR_ESCALATING) || null;
    }
    if (isGroupA(g)) {
      return el.getAttribute(ATTR_ESCALATING) || null;
    }
    return null;
  }

  function isPriceTestArm(g) {
    return isGroupA(g) || isGroupD(g) || isGroupE(g);
  }

  /** Any value we treat as valid for caching / copy. Add Intelligems hash for group C when assigned. */
  var VALID_GROUPS = {
    groupA: true,
    groupB: true,
    groupC: true,
    groupD: true,
    groupE: true,
    groupF: true,
    neutral: true,
    '1b9b21442ba2': true,
    '401fa6708173': true,
    '583d5923a533': true,
    'a8db065641f8': true,
    'a64fff5fac4d': true,
    'b34435223162': true,
    'c8721924c5f9': true,
    'c2f3cc63ce00': true,
    '2b0c64d032fb': true,
    'd55b9f4463dc': true,
    '982eb52ae514': true,
    '8eef2d7bea97': true,
    '7103311f5a7b': true,
  };

  try {
    var _igPriceTest = window.__IG_PRICE_TEST;
    if (_igPriceTest) {
      if (_igPriceTest.groupA) VALID_GROUPS[String(_igPriceTest.groupA).trim()] = true;
      if (_igPriceTest.groupB) VALID_GROUPS[String(_igPriceTest.groupB).trim()] = true;
      if (_igPriceTest.groupC) VALID_GROUPS[String(_igPriceTest.groupC).trim()] = true;
      if (_igPriceTest.groupD) VALID_GROUPS[String(_igPriceTest.groupD).trim()] = true;
      if (_igPriceTest.groupE) VALID_GROUPS[String(_igPriceTest.groupE).trim()] = true;
      if (_igPriceTest.groupF) VALID_GROUPS[String(_igPriceTest.groupF).trim()] = true;
      if (_igPriceTest.groupBId) {
        VALID_GROUPS[String(_igPriceTest.groupBId).trim()] = true;
        GROUP_B_VALUES[String(_igPriceTest.groupBId).trim()] = true;
      }
      if (_igPriceTest.groupCId) {
        VALID_GROUPS[String(_igPriceTest.groupCId).trim()] = true;
        GROUP_C_VALUES[String(_igPriceTest.groupCId).trim()] = true;
      }
      if (_igPriceTest.groupAId) {
        VALID_GROUPS[String(_igPriceTest.groupAId).trim()] = true;
        GROUP_A_VALUES[String(_igPriceTest.groupAId).trim()] = true;
      }
      if (_igPriceTest.groupDId) {
        VALID_GROUPS[String(_igPriceTest.groupDId).trim()] = true;
        GROUP_D_VALUES[String(_igPriceTest.groupDId).trim()] = true;
      }
      if (_igPriceTest.groupEId) {
        VALID_GROUPS[String(_igPriceTest.groupEId).trim()] = true;
        GROUP_E_VALUES[String(_igPriceTest.groupEId).trim()] = true;
      }
      if (_igPriceTest.groupFId) {
        var gfId = String(_igPriceTest.groupFId).trim();
        if (gfId) {
          VALID_GROUPS[gfId] = true;
          GROUP_F_VALUES[gfId] = true;
        }
      }
      if (_igPriceTest.fsControlId) {
        var fsId = String(_igPriceTest.fsControlId).trim();
        if (fsId) {
          VALID_GROUPS[fsId] = true;
          FS_CONTROL_VALUES[fsId] = true;
        }
      }
    }
  } catch (e) {}

  /**
   * Applies free-shipping threshold copy for Control (FS@3) vs groupF (FS@5).
   * Targets [data-ig-fs-text]; optional per-element overrides via
   * data-ig-fs-control-text / data-ig-fs-groupf-text.
   */
  function applyFreeShippingCopy() {
    var g = _cachedGroup;
    var minQty = resolveFreeShipMinQty(g);
    window.__igFreeShipMinQty = minQty;

    document.querySelectorAll('[data-ig-fs-text]').forEach(function (el) {
      var surface = el.getAttribute('data-ig-fs-surface') || 'default';
      if (surface === 'cart' && isGroupB(g) && !isGroupF(g)) {
        el.textContent = '';
        el.style.display = 'none';
        return;
      }
      var fromAttr = isGroupF(g)
        ? el.getAttribute('data-ig-fs-groupf-text')
        : el.getAttribute('data-ig-fs-control-text');
      var text = fromAttr || getFsCopyForSurface(g, surface);
      if (text) {
        el.textContent = text;
        el.style.display = '';
      }
    });

    window.dispatchEvent(
      new CustomEvent('ig:fs-threshold', {
        detail: { group: g, minQty: minQty, isGroupF: isGroupF(g) },
      }),
    );
  }

  /**
   * Applies offer-specific copy. Synchronous — uses cached group, no network call.
   * groupB: default Liquid text; show PDP badge.
   * groupA / groupD / groupE: escalating-style copy; hide PDP badge unless group-specific badge text exists.
   * groupC: data-ig-groupc-text on announcement, cart banner, PDP deal, hero, PDP badge.
   * Free shipping threshold copy runs for B2G1 family (groupB / groupF / control hash).
   */
  function syncGroupCChrome(g) {
    var hideB2g1 = shouldForceDefaultGroupA() || isGroupA(g) || isGroupC(g) || !isGroupB(g);
    try {
      document.documentElement.classList.toggle('ig-offer-hide-b2g1', hideB2g1);
      document.documentElement.classList.toggle('ig-offer-groupc', isGroupC(g));
      document.documentElement.classList.toggle('ig-offer-groupb', isGroupB(g) && !isGroupF(g));
    } catch (e) {}
  }

  function applyEscalatingCopy(el, sel) {
    if (sel === '[data-ig-pdp-badge]') {
      el.style.display = 'none';
      return;
    }
    var altA = el.getAttribute(ATTR_ESCALATING);
    if (altA) {
      el.textContent = altA;
      el.style.display = '';
    }
  }

  function applySpringCopy() {
    var g = _cachedGroup;
    syncGroupCChrome(g);
    if (isB2g1FamilyCart(g) || g == null || g === '') {
      applyFreeShippingCopy();
    }

    var targets = [
      '[data-ig-announcement-text]',
      '[data-cart-promo-banner]',
      '[data-ig-pdp-deal]',
      '[data-ig-pdp-badge]',
      '[data-ig-hero-title]',
    ];

    targets.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (isGroupC(g)) {
          if (sel === '[data-ig-pdp-badge]') {
            var altCBadge = el.getAttribute(ATTR_GROUPC);
            if (altCBadge) {
              el.textContent = altCBadge;
              el.style.display = '';
            } else {
              el.style.display = 'none';
            }
            return;
          }
          if (
            sel === '[data-ig-announcement-text]' ||
            sel === '[data-cart-promo-banner]' ||
            sel === '[data-ig-pdp-deal]' ||
            sel === '[data-ig-hero-title]'
          ) {
            var altC = el.getAttribute(ATTR_GROUPC);
            if (altC) {
              el.textContent = altC;
              el.style.display = '';
            }
          }
          return;
        }
        if (isGroupB(g) || isGroupF(g) || isFsControl(g)) {
          if (sel === '[data-ig-announcement-text]' || sel === '[data-ig-hero-title]') {
            var altB = el.getAttribute('data-ig-groupb-text');
            if (altB) {
              el.textContent = altB;
              el.style.display = '';
            }
          }
          return;
        }
        if (isGroupE(g) || isGroupD(g)) {
          if (sel === '[data-ig-pdp-badge]') {
            var altBadge = isGroupE(g)
              ? el.getAttribute(ATTR_GROUPE)
              : el.getAttribute(ATTR_GROUPD);
            if (!altBadge) altBadge = el.getAttribute(ATTR_ESCALATING);
            if (altBadge) {
              el.textContent = altBadge;
              el.style.display = '';
            } else {
              el.style.display = 'none';
            }
            return;
          }
          var altPriceTest = getPriceTestPromoText(el, g);
          if (altPriceTest) {
            el.textContent = altPriceTest;
            el.style.display = '';
          }
          return;
        }
        applyEscalatingCopy(el, sel);
      });
    });

    if (isPriceTestArm(g) && !document.querySelector('[data-ig-hero-title]')) {
      setTimeout(function () {
        var h = document.querySelector('[data-ig-hero-title]');
        if (!h) return;
        if (isGroupC(g)) {
          var altC2 = h.getAttribute(ATTR_GROUPC);
          if (altC2) h.textContent = altC2;
        } else {
          var altHero = getPriceTestPromoText(h, g);
          if (altHero) h.textContent = altHero;
        }
      }, 500);
    }
  }

  function getPreviewGroupFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get('igTestGroups') || params.get('igTestGroup') || null;
    } catch (e) {
      return null;
    }
  }

  function getStoredPreviewGroup() {
    var urlGroup = getPreviewGroupFromUrl();
    if (urlGroup) return urlGroup;
    try {
      return sessionStorage.getItem('igTestGroup') || sessionStorage.getItem('igTestGroupsPreview') || null;
    } catch (e) {
      return null;
    }
  }

  function shouldForceDefaultGroupA() {
    try {
      var cfg = window.__IG_PRICE_TEST;
      if (!cfg || cfg.forceDefaultGroupA === undefined || cfg.forceDefaultGroupA === null) {
        return true;
      }
      return cfg.forceDefaultGroupA === true || cfg.forceDefaultGroupA === 'true';
    } catch (e) {
      return true;
    }
  }

  function shouldForceDefaultGroupB() {
    try {
      var cfg = window.__IG_PRICE_TEST;
      if (!cfg || cfg.forceDefaultGroupB === undefined || cfg.forceDefaultGroupB === null) {
        return false;
      }
      return cfg.forceDefaultGroupB === true || cfg.forceDefaultGroupB === 'true';
    } catch (e) {
      return false;
    }
  }

  function shouldForceDefaultGroupC() {
    try {
      var cfg = window.__IG_PRICE_TEST;
      if (!cfg || cfg.forceDefaultGroupC === undefined || cfg.forceDefaultGroupC === null) {
        return false;
      }
      return cfg.forceDefaultGroupC === true || cfg.forceDefaultGroupC === 'true';
    } catch (e) {
      return false;
    }
  }

  function seedCartIgTestGroups(group) {
    _cachedGroup = group;
    try {
      sessionStorage.setItem('igTestGroup', group);
      // Drop stale preview key so old groupD/A/C does not re-seed later.
      sessionStorage.setItem('igTestGroupsPreview', group);
    } catch (e) {}
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { igTestGroups: group } }),
    })
      .then(function () {
        applySpringCopy();
        notifyCartGroupUpdated();
      })
      .catch(function () {
        applySpringCopy();
      });
  }

  /**
   * Fetch cart once to seed the cached group, then apply copy.
   *
   * Priority:
   *   1) URL ?igTestGroups=… (QA preview)
   *   2) Force groupA — seed groupA only when cart attribute is empty;
   *      never overwrite other live Intelligems hashes
   *   3) Force groupC / groupB (legacy)
   *   4) Keep existing cart attribute
   *
   * Copy + hide-B2G1: any non-groupC value uses escalating (groupA) messaging.
   */
  function loadGroupFromCart() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var g = cart.attributes && cart.attributes.igTestGroups;
        var urlPreview = getPreviewGroupFromUrl();

        if (urlPreview) {
          return seedCartIgTestGroups(urlPreview);
        }

        if (shouldForceDefaultGroupA()) {
          // Keep other live Intelligems hashes. Only seed groupA when the cart
          // has no igTestGroups yet. Checkout still applies escalating for any
          // non-groupC value.
          if (!g) {
            return seedCartIgTestGroups('groupA');
          }
          _cachedGroup = g;
          try { sessionStorage.setItem('igTestGroup', g); } catch (e) {}
          applySpringCopy();
          return;
        }

        if (shouldForceDefaultGroupC()) {
          if (!g || !isGroupC(g)) {
            return seedCartIgTestGroups('groupC');
          }
          _cachedGroup = g;
          try { sessionStorage.setItem('igTestGroup', g); } catch (e) {}
          applySpringCopy();
          return;
        }

        if (shouldForceDefaultGroupB()) {
          // Preserve Intelligems FS A/B + groupB comma hashes; only wipe A/C/D/E leftovers.
          if (!g || !isB2g1FamilyCart(g)) {
            return seedCartIgTestGroups('groupB');
          }
          _cachedGroup = g;
          try { sessionStorage.setItem('igTestGroup', g); } catch (e) {}
          applySpringCopy();
          return;
        }

        if (g != null && g !== '') {
          _cachedGroup = g;
          try { sessionStorage.setItem('igTestGroup', g); } catch (e) {}
          applySpringCopy();
          return;
        }

        var preview = getStoredPreviewGroup();
        if (preview) {
          return seedCartIgTestGroups(preview);
        }

        applySpringCopy();
      })
      .catch(function () {});
  }

  function activate(offerKey) {
    window.__igActiveOffer = offerKey;
    var config = OFFER_CONFIGS[offerKey];
    if (!config) return;

    syncCartAttributeForOffer(offerKey).then(function () {
      applySpringCopy();
    });

    document.querySelectorAll('[data-ig-offer]').forEach(function (el) {
      el.style.display = el.getAttribute('data-ig-offer') === offerKey ? '' : 'none';
    });

    if (offerKey === 'buy_2_get_1' || offerKey === 'buy_3_get_1') {
      if (window.BuyXGetYFreeManager) {
        window.BuyXGetYFreeManager.overrideConfig({
          quantity: config.triggerQty,
          max_free_items: config.maxFreeItems,
        });
        window.BuyXGetYFreeManager.sync();
      }
    }

    if (offerKey === 'escalating_bundles') {
      document.querySelectorAll('.bundle-tier-selector').forEach(function (container) {
        if (!container._bundleTierInitialized && typeof BundleTierSelector !== 'undefined') {
          new BundleTierSelector(container);
          container._bundleTierInitialized = true;
        }
      });
    }

    window.dispatchEvent(
      new CustomEvent('ig:offer-assigned', {
        detail: { offer: offerKey, config: config },
      }),
    );
  }

  function detect() {
    if (!window.igData || typeof window.igData.user.getTestGroup !== 'function') {
      return null;
    }

    if (!String(TEST_1_ID).startsWith('__REPLACE')) {
      var g1 = window.igData.user.getTestGroup(TEST_1_ID);
      if (g1 && g1.id && TEST_1_GROUPS[g1.id]) {
        return TEST_1_GROUPS[g1.id];
      }
    }

    if (!String(TEST_2_ID).startsWith('__REPLACE')) {
      var g2 = window.igData.user.getTestGroup(TEST_2_ID);
      if (g2 && g2.id && TEST_2_GROUPS[g2.id]) {
        return TEST_2_GROUPS[g2.id];
      }
    }

    return null;
  }

  function init() {
    var offer = detect();
    if (offer) {
      activate(offer);
    } else {
      delete window.__igActiveOffer;
      loadGroupFromCart();
    }
  }

  // Seed copy early: URL preview wins; force A then C then B; else session.
  try {
    var earlyGroup = getPreviewGroupFromUrl();
    if (!earlyGroup && shouldForceDefaultGroupA()) {
      earlyGroup = 'groupA';
    } else if (!earlyGroup && shouldForceDefaultGroupC()) {
      earlyGroup = 'groupC';
    } else if (!earlyGroup && shouldForceDefaultGroupB()) {
      earlyGroup = 'groupB';
    } else if (!earlyGroup) {
      earlyGroup = getStoredPreviewGroup();
    }
    if (earlyGroup) {
      _cachedGroup = earlyGroup;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applySpringCopy);
      } else {
        applySpringCopy();
      }
    }
  } catch (e) {}

  // Synchronous re-apply after any DOM replacement
  document.addEventListener('cart:updated', applySpringCopy);
  document.addEventListener('cart-drawer:opened', applySpringCopy);

  // Re-apply once all page content (including lazy sections) has loaded
  window.addEventListener('load', applySpringCopy);

  if (typeof subscribe !== 'undefined') {
    subscribe('cart-update', applySpringCopy);
  }

  if (window.igData) {
    init();
  } else {
    window.addEventListener('ig:ready', init);
    setTimeout(function () {
      if (!window.__igActiveOffer && !experimentsArePlaceholders()) {
        console.warn('[IG Orchestrator] Intelligems not ready; cart attribute unchanged.');
      }
      loadGroupFromCart();
    }, 3000);
  }

  // Expose the synchronous version globally — used by cart-drawer.js after DOM swaps
  window.applySpringCopyFromCart = applySpringCopy;
})();
